import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { DevLogger } from "@/lib/dev-logger";

interface StudyActivity {
  type: 'document' | 'conversation' | 'video' | 'notes' | 'agent' | 'general';
  startTime: Date;
  endTime?: Date;
  metadata?: Record<string, any>;
}

interface UseStudyTimeTrackerProps {
  topicId: string;
  sessionId?: string;
  onTimeUpdate?: (totalSeconds: number) => void;
}

export const useStudyTimeTracker = ({ 
  topicId, 
  sessionId, 
  onTimeUpdate 
}: UseStudyTimeTrackerProps) => {
  const { user } = useAuth();
  const [totalStudyTime, setTotalStudyTime] = useState(0);
  const [currentActivity, setCurrentActivity] = useState<StudyActivity | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  
  const saveIntervalRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<Date>(new Date());
  const accumulatedTimeRef = useRef(0);

  // Save study time to database
  const saveStudyTime = useCallback(async (activity: StudyActivity) => {
    if (!user || !activity.endTime) return;

    const duration = Math.floor((activity.endTime.getTime() - activity.startTime.getTime()) / 1000);
    
    // Only save sessions longer than 30 seconds
    if (duration < 30) return;

    try {
      const { error } = await supabase
        .from('study_time_sessions')
        .insert({
          user_id: user.id,
          topic_id: topicId,
          session_id: sessionId,
          activity_type: activity.type,
          start_time: activity.startTime.toISOString(),
          end_time: activity.endTime.toISOString(),
          duration_seconds: duration,
          metadata: activity.metadata || {}
        });

      if (error) {
        DevLogger.error('Failed to save study time:', error);
      } else {
        DevLogger.log('Study time saved:', duration, 'seconds for', activity.type);
      }
    } catch (error) {
      DevLogger.error('Error saving study time:', error);
    }
  }, [user, topicId, sessionId]);

  // Start tracking a new activity
  const startActivity = useCallback((type: StudyActivity['type'], metadata?: Record<string, any>) => {
    // End current activity if one exists
    if (currentActivity) {
      const endTime = new Date();
      const completedActivity: StudyActivity = {
        ...currentActivity,
        endTime
      };
      const duration = Math.floor((endTime.getTime() - currentActivity.startTime.getTime()) / 1000);
      const newTotal = totalStudyTime + duration;
      setTotalStudyTime(newTotal);
      saveStudyTime(completedActivity);
      onTimeUpdate?.(newTotal);
    }

    const activity: StudyActivity = {
      type,
      startTime: new Date(),
      metadata
    };

    setCurrentActivity(activity);
    setIsTracking(true);
    lastActivityRef.current = new Date();
    
    DevLogger.log('Started tracking activity:', type);
  }, [currentActivity, totalStudyTime, saveStudyTime, onTimeUpdate]);

  // End the current activity
  const endCurrentActivity = useCallback(() => {
    if (!currentActivity) return;

    const endTime = new Date();
    const completedActivity: StudyActivity = {
      ...currentActivity,
      endTime
    };

    // Calculate duration and add to total
    const duration = Math.floor((endTime.getTime() - currentActivity.startTime.getTime()) / 1000);
    const newTotal = totalStudyTime + duration;
    
    setTotalStudyTime(newTotal);
    setCurrentActivity(null);
    setIsTracking(false);
    
    // Save to database
    saveStudyTime(completedActivity);
    
    // Call callback if provided
    onTimeUpdate?.(newTotal);
    
    DevLogger.log('Ended activity. Duration:', duration, 'seconds. Total:', newTotal);
  }, [currentActivity, totalStudyTime, saveStudyTime, onTimeUpdate]);

  // Register user activity (mouse, keyboard, scroll)
  const registerActivity = useCallback(() => {
    lastActivityRef.current = new Date();
  }, []);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    if (!isTracking) return;

    const now = new Date();
    const timeSinceActivity = now.getTime() - lastActivityRef.current.getTime();
    
    // If inactive for more than 3 minutes, pause tracking
    if (timeSinceActivity > 3 * 60 * 1000) {
      DevLogger.log('User inactive, pausing study tracking');
      endCurrentActivity();
    }
  }, [isTracking, endCurrentActivity]);

  // Periodic save (every 30 seconds while active)
  const periodicSave = useCallback(() => {
    if (!currentActivity || !isTracking) return;

    // Update accumulated time
    const now = new Date();
    const sessionDuration = Math.floor((now.getTime() - currentActivity.startTime.getTime()) / 1000);
    const newTotal = totalStudyTime + sessionDuration;
    
    setTotalStudyTime(newTotal);
    onTimeUpdate?.(newTotal);
  }, [currentActivity, isTracking, totalStudyTime, onTimeUpdate]);

  // Format time helper
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  // Setup activity listeners
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      document.addEventListener(event, registerActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, registerActivity);
      });
    };
  }, [registerActivity]);

  // Setup inactivity checker
  useEffect(() => {
    const inactivityInterval = setInterval(checkInactivity, 30000); // Check every 30 seconds
    
    return () => clearInterval(inactivityInterval);
  }, [checkInactivity]);

  // Setup periodic save
  useEffect(() => {
    if (isTracking) {
      saveIntervalRef.current = setInterval(periodicSave, 30000); // Save every 30 seconds
    } else {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    }

    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    };
  }, [isTracking, periodicSave]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        DevLogger.log('Page hidden, pausing study tracking');
        endCurrentActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [endCurrentActivity]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentActivity) {
        endCurrentActivity();
      }
    };
  }, []);

  return {
    totalStudyTime,
    currentActivity,
    isTracking,
    startActivity,
    endCurrentActivity,
    registerActivity,
    formatTime
  };
};